// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useEffect, useState } from "react";

import {
  Circle,
  CircleCheck,
  Database,
  Dot,
  GitBranch,
  History,
  LockKeyhole,
  Pause,
  Play,
  RefreshCw,
  Square,
} from "lucide-react";

import type {
  ChronicleBackfill,
  ChronicleBackfillResult,
  ChronicleReadiness,
  CockpitApi,
  CockpitIdentity,
} from "./api.js";
import { displayState, pluralize, stateTone } from "./presentation.js";

const SAMPLE_FILES = JSON.stringify(
  [
    {
      byteSize: 57,
      content: "# Project\n\nReplace this fixture with repository content.\n",
      encoding: "utf-8",
      path: "README.md",
      type: "file",
    },
  ],
  null,
  2,
);

export function Onboarding({
  api,
  identity,
}: {
  readonly api: CockpitApi;
  readonly identity: CockpitIdentity;
}) {
  const [connectionId, setConnectionId] = useState<string>(crypto.randomUUID());
  const [displayName, setDisplayName] = useState("repository");
  const [provider, setProvider] = useState<"git" | "github">("git");
  const [providerRepositoryId, setProviderRepositoryId] =
    useState("repository-1");
  const [repositoryUrl, setRepositoryUrl] = useState(
    "https://github.com/example/repository",
  );
  const [credentialSecret, setCredentialSecret] = useState("");
  const [snapshotFiles, setSnapshotFiles] = useState(SAMPLE_FILES);
  const [repositoryRevision, setRepositoryRevision] = useState("a".repeat(40));
  const [readiness, setReadiness] = useState<ChronicleReadiness | null>(null);
  const [backfill, setBackfill] = useState<ChronicleBackfill | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const [nextReadiness, nextBackfill] = await Promise.allSettled([
      api.readiness(),
      api.backfill(),
    ]);
    if (nextReadiness.status === "fulfilled") {
      setReadiness(nextReadiness.value);
    }
    if (nextBackfill.status === "fulfilled") {
      setBackfill(nextBackfill.value.backfill);
    }
    if (nextReadiness.status === "rejected") {
      setStatus(
        nextReadiness.reason instanceof Error
          ? nextReadiness.reason.message
          : String(nextReadiness.reason),
      );
    }
  }

  useEffect(() => {
    void refresh();
  }, [api]);

  async function connectRepository() {
    setBusy(true);
    setStatus("Creating an encrypted credential reference…");
    try {
      const credential = await api.command<{
        readonly credentialReference: {
          readonly credentialReferenceId: string;
        };
      }>("createCredentialReference", {
        body: {
          purpose: `${provider} repository read access`,
          secret: credentialSecret,
        },
      });
      setStatus("Connecting the repository to this Project…");
      await api.command("connectRepository", {
        body: {
          connectionId,
          credentialReferenceId:
            credential.credentialReference.credentialReferenceId,
          displayName,
          provider,
          providerRepositoryId,
          repositoryUrl,
        },
        path: { projectId: identity.projectId },
      });
      setCredentialSecret("");
      setStatus("Repository connected. Current inventory can now be ingested.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function onboardCurrentCode() {
    setBusy(true);
    setStatus("Recording current repository inventory and calibration…");
    try {
      const files: unknown = JSON.parse(snapshotFiles);
      if (!Array.isArray(files)) {
        throw new TypeError("Snapshot files must be a JSON array");
      }
      await api.command("onboardChronicleCurrentCode", {
        body: {
          files,
          observedAt: new Date().toISOString(),
          repositoryConnectionId: connectionId,
          repositoryRevision,
          snapshotId: `snapshot:${crypto.randomUUID()}`,
        },
        path: { projectId: identity.projectId },
      });
      await refresh();
      setStatus("Current inventory and calibration are ready.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function startBackfill() {
    setBusy(true);
    setStatus("Starting checkpointed accessible-history backfill…");
    try {
      const result = await api.command<ChronicleBackfillResult>(
        "startChronicleBackfill",
        {
          body: {
            backfillId: `backfill:${crypto.randomUUID()}`,
            sourceScopeRevision: (backfill?.sourceScopeRevision ?? 0) + 1,
            sources: [
              {
                kind: provider,
                repositoryConnectionId: connectionId,
                selectedKinds:
                  provider === "github"
                    ? [
                        "ci-outcome",
                        "issue",
                        "pull-request",
                        "release",
                        "review-event",
                        "review-thread",
                      ]
                    : [
                        "branch",
                        "commit",
                        "documentation-version",
                        "release",
                        "tag",
                      ],
                sourceId: `${provider}-history`,
              },
            ],
          },
          path: { projectId: identity.projectId },
        },
      );
      setBackfill(result.backfill);
      setReadiness(result.readiness);
      setStatus(
        "Backfill started. Reviews remain available with honest coverage.",
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function controlBackfill(action: "cancel" | "pause" | "resume") {
    if (backfill === null) {
      return;
    }
    setBusy(true);
    setStatus(`${backfillActionLabel(action)} accessible-history backfill…`);
    try {
      const result = await api.controlBackfill(
        backfill.backfillId,
        action,
        backfill.revision,
        `cockpit-${action}`,
      );
      setBackfill(result.backfill);
      setReadiness(result.readiness);
      setStatus(
        action === "pause"
          ? "Backfill paused at its durable checkpoint."
          : action === "resume"
            ? "Backfill resumed from its durable checkpoint."
            : "Backfill cancelled without discarding recorded history.",
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function advanceBackfill() {
    if (backfill === null) {
      return;
    }
    setBusy(true);
    setStatus("Ingesting one bounded page of accessible history…");
    try {
      const result = await api.advanceBackfill(backfill.backfillId, 100);
      setBackfill(result.backfill);
      setReadiness(result.readiness);
      setStatus(
        result.backfill.state === "completed"
          ? "Accessible-history backfill completed."
          : "Backfill checkpoint advanced; remaining coverage is visible below.",
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Repositories</h1>
          <p>
            Connect a repository, capture the code as it stands today, then
            import as much history as the source will give you. Whatever cannot
            be read is recorded rather than skipped.
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className="button secondary"
            onClick={() => void refresh()}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {/* Progress is named by the work itself, never enumerated as Step 1/2/3. */}
      <div className="progress-track" aria-label="Setup progress">
        <ProgressStep
          active
          complete={readiness !== null}
          label="Project configured"
        />
        <ProgressStep
          active={readiness?.currentCode !== "current-code-ready"}
          complete={readiness?.currentCode === "current-code-ready"}
          label="Current code captured"
        />
        <ProgressStep
          active={readiness?.historicalBackfill === "in-progress"}
          complete={readiness?.historicalBackfill === "complete"}
          label="History imported"
        />
      </div>

      <section className="panel onboarding-step">
        <div className="panel-head">
          <div>
            <h2>Connect a repository</h2>
            <p>
              Diffclaw stores a reference to your credential, not the credential
              itself, and never displays it again.
            </p>
          </div>
          <span className="security-label">
            <LockKeyhole size={14} aria-hidden="true" />
            Write access is never requested
          </span>
        </div>
        <div className="panel-pad stack">
          <div className="form-grid">
            <label>
              Provider
              <select
                value={provider}
                onChange={(event) => {
                  setProvider(event.target.value as "git" | "github");
                }}
              >
                <option value="git">Git source</option>
                <option value="github">GitHub App source</option>
              </select>
            </label>
            <label>
              Name this repository
              <input
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                }}
              />
              <span className="field-help">
                Shown wherever this repository appears in reviews.
              </span>
            </label>
            <label className="wide">
              Repository URL
              <input
                type="url"
                value={repositoryUrl}
                onChange={(event) => {
                  setRepositoryUrl(event.target.value);
                }}
              />
            </label>
            <label className="wide">
              Read credential
              <input
                autoComplete="off"
                type="password"
                value={credentialSecret}
                onChange={(event) => {
                  setCredentialSecret(event.target.value);
                }}
              />
              <span className="field-help">
                A read-only token or deploy key. It is encrypted on arrival and
                never shown again.
              </span>
            </label>
          </div>
          {/*
          Machine identifiers a human should not have to invent. They are
          generated and editable, but kept out of the primary form.
        */}
          <details className="disclosure">
            <summary>Advanced connection identifiers</summary>
            <div className="form-grid">
              <label>
                Connection ID
                <input
                  className="code-input"
                  value={connectionId}
                  onChange={(event) => {
                    setConnectionId(event.target.value);
                  }}
                />
              </label>
              <label>
                Provider repository ID
                <input
                  className="code-input"
                  value={providerRepositoryId}
                  onChange={(event) => {
                    setProviderRepositoryId(event.target.value);
                  }}
                />
              </label>
            </div>
          </details>
          <button
            className="button primary"
            disabled={busy || credentialSecret.length === 0}
            onClick={() => void connectRepository()}
            type="button"
          >
            <GitBranch size={16} aria-hidden="true" />
            Connect repository
          </button>
        </div>
      </section>
      <section className="panel onboarding-step">
        <div className="panel-head">
          <div>
            <h2>Capture the code as it stands</h2>
            <p>
              This snapshot is what reviews are compared against. Nothing is
              inferred from it that the files do not actually say.
            </p>
          </div>
          <span
            className={`state-badge tone-${stateTone(readiness?.currentCode ?? "not-started")}`}
          >
            {displayState(readiness?.currentCode ?? "not-started")}
          </span>
        </div>
        <div className="panel-pad stack">
          <label>
            Repository revision
            <input
              className="code-input"
              value={repositoryRevision}
              onChange={(event) => {
                setRepositoryRevision(event.target.value);
              }}
            />
          </label>
          <label>
            Snapshot files
            <textarea
              className="code-input"
              rows={10}
              value={snapshotFiles}
              onChange={(event) => {
                setSnapshotFiles(event.target.value);
              }}
            />
            <span className="field-help">
              JSON array of file records, each with a path, content, and
              encoding.
            </span>
          </label>
          <button
            className="button primary"
            disabled={busy}
            onClick={() => void onboardCurrentCode()}
            type="button"
          >
            <Database size={16} aria-hidden="true" />
            Capture this revision
          </button>
        </div>
      </section>
      <section className="panel onboarding-step">
        <div className="panel-head">
          <div>
            <h2>Import history</h2>
            <p>
              Commits and past review activity give a change its context. Rate
              limits, unreachable ranges, and ambiguity are all recorded, and an
              import in progress never blocks review of current code.
            </p>
          </div>
          <span
            className={`state-badge tone-${stateTone(readiness?.historicalBackfill ?? "not-started")}`}
          >
            {displayState(readiness?.historicalBackfill ?? "not-started")}
          </span>
        </div>
        <div className="panel-pad stack">
          {backfill === null ? (
            <button
              className="button primary"
              disabled={busy || readiness?.currentCode !== "current-code-ready"}
              onClick={() => void startBackfill()}
              type="button"
            >
              <History size={16} aria-hidden="true" />
              Start importing history
            </button>
          ) : (
            <div className="stack">
              <div className="metric-row">
                <div className="metric">
                  <span className="metric-label">Import state</span>
                  <span className="metric-value" data-testid="backfill-state">
                    <small>{displayState(backfill.state)}</small>
                  </span>
                </div>
                <div className="metric">
                  <span className="metric-label">Revision</span>
                  <span className="metric-value">{backfill.revision}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Knowledge version</span>
                  <span className="metric-value">
                    {backfill.knowledgeWatermark}
                  </span>
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <caption className="sr-only">
                    Coverage of each historical source
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      <th scope="col">State</th>
                      <th scope="col">Ingested</th>
                      <th scope="col">Discovered</th>
                      <th scope="col">Checkpoint</th>
                      <th scope="col">Rate limit</th>
                      <th scope="col">Gaps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backfill.sources.map((source) => (
                      <tr key={source.sourceId}>
                        <th scope="row">
                          {source.sourceId}
                          <small>{source.kind}</small>
                        </th>
                        <td>
                          <span
                            className={`state-badge tone-${stateTone(source.state)}`}
                          >
                            {displayState(source.state)}
                          </span>
                        </td>
                        <td>{source.ingested}</td>
                        <td>{source.discovered}</td>
                        <td>{source.cursor ?? "not checkpointed"}</td>
                        <td>
                          {source.rateLimit === null
                            ? "available"
                            : `${source.rateLimit.reason}; resets ${source.rateLimit.resetAt}`}
                        </td>
                        <td>
                          {source.inaccessibleIntervals.length === 0
                            ? "none recorded"
                            : `${String(source.inaccessibleIntervals.length)} unreachable ${pluralize(source.inaccessibleIntervals.length, "range")}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="button-row">
                {backfill.state === "running" ? (
                  <>
                    <button
                      className="button primary"
                      disabled={busy}
                      onClick={() => void advanceBackfill()}
                      type="button"
                    >
                      <RefreshCw size={16} aria-hidden="true" />
                      Import the next page
                    </button>
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={() => void controlBackfill("pause")}
                      type="button"
                    >
                      <Pause size={16} aria-hidden="true" />
                      Pause
                    </button>
                  </>
                ) : backfill.state === "paused" ||
                  backfill.state === "degraded" ? (
                  <button
                    className="button primary"
                    disabled={busy}
                    onClick={() => void controlBackfill("resume")}
                    type="button"
                  >
                    <Play size={16} aria-hidden="true" />
                    Resume
                  </button>
                ) : null}
                {backfill.state !== "completed" &&
                backfill.state !== "cancelled" ? (
                  <button
                    className="button danger-quiet"
                    disabled={busy}
                    onClick={() => void controlBackfill("cancel")}
                    type="button"
                  >
                    <Square size={16} aria-hidden="true" />
                    Cancel import
                  </button>
                ) : null}
                {backfill.state === "cancelled" ? (
                  <button
                    className="button primary"
                    disabled={busy}
                    onClick={() => void startBackfill()}
                    type="button"
                  >
                    <RefreshCw size={16} aria-hidden="true" />
                    Start again
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </section>
      <p className="status-line" aria-live="polite">
        {status}
      </p>
    </div>
  );
}

function backfillActionLabel(action: "cancel" | "pause" | "resume"): string {
  return action === "pause"
    ? "Pausing"
    : action === "resume"
      ? "Resuming"
      : "Cancelling";
}

function ProgressStep({
  active,
  complete,
  label,
}: {
  readonly active: boolean;
  readonly complete: boolean;
  readonly label: string;
}) {
  return (
    <div
      className={`progress-step ${complete ? "done" : active ? "current" : ""}`}
    >
      {complete ? (
        <CircleCheck size={16} aria-hidden="true" />
      ) : active ? (
        <Dot size={16} aria-hidden="true" />
      ) : (
        <Circle size={16} aria-hidden="true" />
      )}
      {label}
    </div>
  );
}
